import { lazy, Suspense } from "react";
import { Navigate, Route, Routes } from "react-router";

import { LoadingState, RouteErrorBoundary } from "@/components/common";
import { AppShell } from "@/components/layout";
import {
  AdminRoute,
  ApproversRoute,
  MigratorRoute,
  ProtectedRoute,
  TeamsRoute,
} from "@/components/protected-route";
import {
  AccessCasePage,
  AccessCasesPage,
  AccessItemPage,
  AccessRequestPage,
  HowApprovingWorksPage,
  MyApprovalsPage,
  StartersRoute,
} from "@/features/access";
import { AskPage } from "@/features/assistant";
import { DemoPersonaPage } from "@/features/demo-persona";
import { EmergencyPage, SuspiciousEmailPage } from "@/features/emergency";
import { AdminFeedbackPage } from "@/features/feedback";
import {
  AssignPage,
  EmailIntakePage,
  ItAdminHubPage,
  ItAdminLayout,
  ItTrainingLessonPage,
  ItTrainingPage,
  PersonProfilePage,
  RoutingPage,
  TicketSearchPage,
} from "@/features/it-admin";
import { KioskGate } from "@/features/kiosk";
import {
  KnowledgeAreasPage,
  KnowledgeArticlePage,
  KnowledgeArticlesPage,
  KnowledgeBulkAddPage,
  KnowledgeOverviewPage,
  KnowledgeReadPage,
} from "@/features/knowledge";
import { LeadershipReportRoute } from "@/features/leadership-report";
import { AccessTrainingPage, AccessTrainingProgressPage } from "@/features/learn";
import { LoginPage } from "@/features/login";
import { AdminMessagesPage, MessageDetailPage, MessagesPage } from "@/features/messages";
import { MigratePage } from "@/features/migrate";
import { NotFoundPage } from "@/features/not-found";
import { StarterCasePage } from "@/features/starters";
import { TeamDetailPage, TeamsPage } from "@/features/teams";
import { TeamsBotPage } from "@/features/teams-bot";
import {
  TicketCreatePage,
  TicketDetailPage,
  TicketListPage,
  TicketNumberRedirectPage,
} from "@/features/tickets";
import { ViewAsPage } from "@/features/view-as";

/** Lazy, so the chart kit it brings reaches only the admins who open it. */
const ApproversPage = lazy(() =>
  import("@/features/approvers").then((module) => ({ default: module.ApproversPage })),
);
const AdminUsagePage = lazy(() =>
  import("@/features/usage").then((module) => ({ default: module.AdminUsagePage })),
);

/**
 * Route table.
 *
 * `/ask` is the front door: an employee's first move is a question in their own
 * words, not a form. `/` and the legacy `/home` both land there.
 *
 * Everything except `/login` sits inside `<ProtectedRoute>` (session gate),
 * then `<KioskGate>` (on a shared device, "who are you?" first; a pass-through
 * otherwise), then `<AppShell>` (the layout route that renders the header once
 * and the page into its `<Outlet>`).
 */
export function App() {
  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />

      <Route element={<ProtectedRoute />}>
        <Route element={<KioskGate />}>
          <Route element={<AppShell />}>
            <Route path="/" element={<Navigate to="/ask" replace />} />
            {/* Kept because the login flow and AdminRoute still navigate to /home. */}
            <Route path="/home" element={<Navigate to="/ask" replace />} />

            <Route path="/ask" element={<AskPage />} />

            <Route path="/tickets" element={<TicketListPage />} />
            {/* Declared before the dynamic segment for readability; React Router 7
              ranks the static path higher regardless of order. */}
            <Route path="/tickets/new" element={<TicketCreatePage />} />
            {/* Links in Ivanti notification emails carry the ticket *number*;
              this resolves it and replaces itself with /tickets/:ticketId.
              `/t/:number` is the short form. The alias cannot collide with
              `/tickets/:ticketId`: it is three segments to that route's two,
              and React Router ranks by specificity, not declaration order. */}
            <Route path="/t/:number" element={<TicketNumberRedirectPage />} />
            <Route path="/tickets/by-number/:number" element={<TicketNumberRedirectPage />} />
            <Route path="/tickets/:ticketId" element={<TicketDetailPage />} />

            {/* New starters (2 Oct): HR's New User Request and what follows it.
              With the access front door on (5 Oct) the list is Access's,
              filtered to New, and /starters lands there; the case page stays.
              The server decides who may see which. */}
            <Route path="/starters" element={<StartersRoute />} />
            <Route path="/starters/:caseId" element={<StarterCasePage />} />

            {/* The user access front door (5 Oct): "Request access for a person"
              and the Access cases tracker. The server answers 404 while
              ACCESS_FRONT_DOOR_ENABLED is off, and the entries stay hidden. */}
            <Route path="/access" element={<AccessCasesPage />} />
            <Route path="/access/new" element={<AccessRequestPage />} />
            <Route path="/access/:caseId" element={<AccessCasePage />} />
            <Route path="/access/:caseId/items/:n" element={<AccessItemPage />} />
            {/* 19 Oct: the approver's view; the approval email, reminders and
              Teams cards land here (`?case=<id>`). Server-side 404 while the
              front door is off, as every /access route. */}
            <Route path="/approvals" element={<MyApprovalsPage />} />
            {/* 29 Oct: the one-minute guide for approvers, linked from their first
              Teams cards, the bot's welcome card, My approvals and the
              announcement email. Static, so it works with the front door off. */}
            <Route path="/approvals/how-it-works" element={<HowApprovingWorksPage />} />

            {/* IT Admin (5 Oct): the hub (6 Oct, decision 35; User lookup on top)
              for HelpDesk admins and IT teams.
              The layout checks `/auth/me` → `it_admin`; the server refuses
              anyone else (403) and 404s the area while IT_ADMIN_ENABLED is off. */}
            <Route path="/it-admin" element={<ItAdminLayout />}>
              <Route index element={<ItAdminHubPage />} />
              <Route path="training" element={<ItTrainingPage />} />
              <Route path="training/:slug" element={<ItTrainingLessonPage />} />
              <Route path="people/:employeeId" element={<PersonProfilePage />} />
              {/* 16 Oct: "Who should this go to?" and the routing register. */}
              <Route path="assign" element={<AssignPage />} />
              <Route path="routing" element={<RoutingPage />} />
              {/* 19 Oct: email intake (option B), the processed mails and the review queue. */}
              <Route path="email-intake" element={<EmailIntakePage />} />
              {/* 21 Oct: Ticket search, any incident or request by any field. */}
              <Route path="ticket-search" element={<TicketSearchPage />} />
            </Route>

            {/* Access process training (13 Oct): everyone signed in. Reached from
              the chat's access card, the access wizard, the footer and IT
              Admin → Learning; no menu item of its own for everyone else. */}
            <Route path="/learn/access" element={<AccessTrainingPage />} />

            {/* The Leadership report (8 Oct): the IT Leadership Team and Rusty only
              (`me.leadership_report`, LEADERSHIP_REPORT_VIEWERS); every
              `/admin/reports/*` endpoint refuses anyone else, admins included. */}
            <Route path="/leadership-report" element={<LeadershipReportRoute />} />

            {/* IT Admin → Knowledge (9 Oct): Overview and Articles for every IT
              user (`it_admin`), Bulk add and Areas for KNOWLEDGE_EDITORS; the
              server applies the same rule. `/knowledge/read/:id` is what a chat
              citation opens, for anyone signed in (published only). */}
            <Route path="/knowledge" element={<KnowledgeOverviewPage />} />
            <Route path="/knowledge/articles" element={<KnowledgeArticlesPage />} />
            <Route path="/knowledge/articles/:articleId" element={<KnowledgeArticlePage />} />
            <Route path="/knowledge/bulk-add" element={<KnowledgeBulkAddPage />} />
            <Route path="/knowledge/areas" element={<KnowledgeAreasPage />} />
            <Route path="/knowledge/read/:articleId" element={<KnowledgeReadPage />} />

            {/* Linked only from the footer. Quiet by design: an emergency button
              in the header gets used for password resets, and a P1 channel that
              cries wolf is worse than no channel. The footer is where people
              already look for "how do I reach someone", and it is on every
              page — including deep inside a ticket, which is where a situation
              often turns into an emergency. */}
            <Route path="/emergency" element={<EmergencyPage />} />
            {/* 19 Oct (incident 940): one suspicious email, from the Emergency
              form's SECURITY option and from the chat. */}
            <Route path="/report-suspicious-email" element={<SuspiciousEmailPage />} />

            {/* The notice board in full. Reached from the Ask page's Message
              Center and from the footer. */}
            <Route path="/messages" element={<MessagesPage />} />
            <Route path="/messages/:messageId" element={<MessageDetailPage />} />

            {/* Migrators (Systems Support) and admins: legacy ticket -> ITSM. */}
            <Route element={<MigratorRoute />}>
              <Route path="/migrate" element={<MigratePage />} />
            </Route>

            {/* All of IT (read-only), admins and team managers
              (`me.can_view_teams`): Ivanti's Employee Teams. Every `/teams/*`
              endpoint checks again, per team; writes only for an admin or the
              team's own manager. */}
            <Route element={<TeamsRoute />}>
              <Route path="/teams" element={<TeamsPage />} />
              <Route path="/teams/:teamId" element={<TeamDetailPage />} />
            </Route>

            {/* IT Admin → Management → Approvers (8 Oct): the approver matrix, for
              admins and (12 Oct) APPROVERS_EDITOR_TEAMS members (`me.approvers`);
              every `/admin/approvers/*` endpoint applies the same rule. Lazy, like Usage. */}
            {/* Demo personas (19 Oct): test systems only, for the View-as admins.
              Not under AdminRoute: a persona tab (a "user" session) still opens
              it to switch; the page and every endpoint check `can_demo_as`. */}
            <Route path="/admin/demo-personas" element={<DemoPersonaPage />} />

            <Route element={<ApproversRoute />}>
              <Route
                path="/admin/approvers"
                element={
                  <RouteErrorBoundary>
                    <Suspense fallback={<LoadingState />}>
                      <ApproversPage />
                    </Suspense>
                  </RouteErrorBoundary>
                }
              />
            </Route>

            {/* Admin-only. `<AdminRoute>` redirects a non-admin to /home, and every
              `/admin/*` endpoint is independently gated by `require_admin`, so
              the route guard is a convenience, not the security boundary. */}
            <Route element={<AdminRoute />}>
              <Route path="/admin/messages" element={<AdminMessagesPage />} />
              <Route path="/admin/feedback" element={<AdminFeedbackPage />} />
              {/* "View as" (6 Oct): test systems only; the server 404s it in production. */}
              <Route path="/admin/view-as" element={<ViewAsPage />} />
              {/* IT Admin → Learning → Access training progress (13 Oct). */}
              <Route path="/admin/access-training" element={<AccessTrainingProgressPage />} />
              {/* IT Admin → Management → Teams bot (19 Oct). */}
              <Route path="/admin/teams-bot" element={<TeamsBotPage />} />
              {/* Lazy, so its chunk can 404 after a deploy; the boundary turns
                that into a reload offer instead of a blank app. */}
              <Route
                path="/admin/usage"
                element={
                  <RouteErrorBoundary>
                    <Suspense fallback={<LoadingState />}>
                      <AdminUsagePage />
                    </Suspense>
                  </RouteErrorBoundary>
                }
              />
            </Route>
          </Route>
        </Route>
      </Route>

      <Route path="*" element={<NotFoundPage />} />
    </Routes>
  );
}
